// Thin WebSocket client used by the Carrom page. Auto-reconnects and
// re-authenticates with the current access token. Mirrors LudoWS
// 1:1 — same heartbeat, same retry cadence, same auth-fail
// recovery — only the endpoint changes (`/api/carrom/ws`).
import { getAccessToken, api } from '@baaligames/shared-ui/lib/api';

type Listener = (msg: any) => void;
export type WSStatus = 'connecting' | 'open' | 'reconnecting' | 'lost';
type StatusFn = (s: WSStatus) => void;

export class CarromWS {
  private ws: WebSocket | null = null;
  private listeners = new Set<Listener>();
  private statusListeners = new Set<StatusFn>();
  private closed = false;
  private reconnecting = false;
  private pingTimer: number | null = null;
  // Flag set when the server's last auth attempt failed (either via a
  // `{type:'error', code:'auth_failed'}` frame or a close(4002)). Next
  // auth() will force a refresh instead of resending the stale token.
  private authFailedOnce = false;

  connect() {
    this.closed = false;
    const proto = location.protocol === 'https:' ? 'wss' : 'ws';
    this.emitStatus(this.reconnecting ? 'reconnecting' : 'connecting');
    this.ws = new WebSocket(`${proto}://${location.host}/api/carrom/ws`);
    this.ws.addEventListener('open', () => {
      const wasReconnecting = this.reconnecting;
      this.emitStatus('open');
      this.auth(wasReconnecting).finally(() => { this.reconnecting = false; });
    });
    this.ws.addEventListener('message', (ev) => {
      try {
        const msg = JSON.parse(ev.data);
        if (msg && msg.type === 'error' && msg.code === 'auth_failed') {
          this.authFailedOnce = true;
        }
        for (const l of this.listeners) l(msg);
      } catch {}
    });
    this.ws.addEventListener('close', (ev) => {
      if (this.pingTimer) { clearInterval(this.pingTimer); this.pingTimer = null; }
      if (ev && (ev as CloseEvent).code === 4002) this.authFailedOnce = true;
      if (!this.closed) {
        this.reconnecting = true;
        this.emitStatus('lost');
        // Flat 3 s retry — same as Ludo. Exponential backoff was too
        // slow on flaky mobile connections during the original PHP
        // build, and the same logic carries over here.
        setTimeout(() => this.connect(), 3000);
      }
    });
    this.ws.addEventListener('error', () => {/* swallow; close handler reconnects */});
  }

  private async auth(isReconnect: boolean = false) {
    // Reconnect-with-expired-token guard: a Carrom match can outlast
    // the 15-min access token, so always refresh on a reconnect cycle
    // (or on first auth, or after a known auth_failed). Identical
    // policy to LudoWS — see ws.ts there for the original rationale.
    const needsRefresh = !getAccessToken() || isReconnect || this.authFailedOnce;
    if (needsRefresh) {
      await api.tryRefresh();
      this.authFailedOnce = false;
    }
    const token = getAccessToken();
    if (!token || !this.ws) return;
    this.ws.send(JSON.stringify({ type: 'authenticate', token }));
    // 25 000 ms heartbeat — same as Ludo. Server replies with a pong
    // we ignore; the round-trip is enough to keep proxy timeouts
    // (Cloudflare, nginx idle, etc.) from killing the socket.
    this.pingTimer = window.setInterval(() => this.send({ type: 'ping' }), 25_000);
  }

  onStatus(fn: StatusFn) {
    this.statusListeners.add(fn);
    return () => this.statusListeners.delete(fn);
  }
  private emitStatus(s: WSStatus) { for (const fn of this.statusListeners) fn(s); }

  send(msg: any) {
    if (!this.ws || this.ws.readyState !== 1) return false;
    this.ws.send(JSON.stringify(msg));
    return true;
  }

  on(fn: Listener) {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  close() {
    this.closed = true;
    if (this.pingTimer) { clearInterval(this.pingTimer); this.pingTimer = null; }
    this.ws?.close();
    this.ws = null;
  }
}
